import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getPageVariablesBatch } from '@/services/api/connections';
import { validateBroadcastVariables } from '@/services/api/broadcast';
import { getEsteira } from '@/services/api/utility-esteiras';
import { BroadcastFormData } from '../types/broadcast.types';
import type { Page, ValidateVariablesResponse } from '@/types/broadcast';
import type { ContentBlock } from '@/types/flow';
import type { PageVariable } from '@/pages/Meta/Connection/types/meta.types';
import StepSelectPage from './StepSelectPage';
import StepConfigureBlocks from './StepConfigureBlocks';
import StepSchedule from './StepSchedule';
import StepReview from './StepReview';
import MessengerPreview from './MessengerPreview';
import VariablesCopyDialog from './VariablesCopyDialog';
import StepSelectType from './StepSelectType';
import StepSelectEsteira from './StepSelectEsteira';
import { ChevronLeft, ChevronRight, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

const DEFAULT_FORM_DATA: BroadcastFormData = {
    selectedPages: [],
    blocks: [],
    isScheduled: false,
    scheduledTime: '',
    broadcastType: undefined,
    messageTag: null,
};

interface CreateBroadcastModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmit: (data: BroadcastFormData) => void;
    isLoading: boolean;
    initialData?: BroadcastFormData;
    isEditMode?: boolean;
}

export default function CreateBroadcastModal({
    open,
    onOpenChange,
    onSubmit,
    isLoading,
    initialData,
    isEditMode = false,
}: CreateBroadcastModalProps) {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<BroadcastFormData>(DEFAULT_FORM_DATA);
    const [validating, setValidating] = useState(false);
    const [validationResult, setValidationResult] = useState<ValidateVariablesResponse | null>(null);
    const [showCopyDialog, setShowCopyDialog] = useState(false);

    const isUtility = formData.broadcastType === 'utility';
    const isEsteira = formData.broadcastType === 'esteira';
    const useEsteira = formData.selectedEsteiraId != null;

    // Carrega o detalhe da Esteira selecionada — usada para montar o payload
    // final (utility_template_resolution + auxiliar) e o resumo em StepReview.
    const { data: selectedEsteira } = useQuery({
        queryKey: ['utility-esteira', formData.selectedEsteiraId],
        queryFn: () => getEsteira(formData.selectedEsteiraId as number),
        enabled: useEsteira,
    });

    const pageIds = formData.selectedPages.map(p => p.id).filter((id): id is number => typeof id === 'number');
    const { data: batchVariables } = useQuery({
        queryKey: ['page-variables-batch', pageIds],
        queryFn: () => getPageVariablesBatch(pageIds),
        enabled: pageIds.length > 0,
        staleTime: 1000 * 60 * 2,
    });
    const pageVariables = useMemo((): PageVariable[] => {
        if (!batchVariables) return [];
        const seen = new Map<string, PageVariable>();
        for (const vars of Object.values(batchVariables)) {
            for (const v of vars) {
                if (!seen.has(v.key)) seen.set(v.key, v);
            }
        }
        return Array.from(seen.values());
    }, [batchVariables]);

    useEffect(() => {
        if (open) {
            setFormData(initialData || DEFAULT_FORM_DATA);
            setCurrentStep(initialData ? 1 : 0);
            setValidationResult(null);
        }
    }, [open, initialData]);

    const steps = isUtility || isEsteira
        ? [
            { number: 1, title: isEsteira ? 'Esteira' : 'Template', description: isEsteira ? 'Escolha a Esteira' : 'Escolha o template' },
            { number: 2, title: 'Agendar', description: 'Escolha quando enviar' },
            { number: 3, title: 'Revisar', description: 'Confirme os detalhes' },
        ]
        : [
            { number: 1, title: 'Páginas', description: 'Escolha as páginas' },
            { number: 2, title: 'Conteúdo', description: 'Monte a mensagem' },
            { number: 3, title: 'Agendar', description: 'Escolha quando enviar' },
            { number: 4, title: 'Revisar', description: 'Confirme os detalhes' },
        ];

    const totalSteps = steps.length;

    const handleNext = async () => {
        if (currentStep === 0) {
            setCurrentStep(1);
            return;
        }

        if (!isUtility && currentStep === 2 && formData.selectedPages.length > 0 && formData.blocks.length > 0) {
            setValidating(true);
            try {
                const result = await validateBroadcastVariables({
                    page_ids: formData.selectedPages.map(p => p.id).filter((id): id is number => typeof id === 'number'),
                    blocks: formData.blocks,
                });
                setValidationResult(result);
                if (result && !result.all_resolved) {
                    setShowCopyDialog(true);
                    setValidating(false);
                    return;
                }
            } catch {
                setValidationResult(null);
            } finally {
                setValidating(false);
            }
        }

        if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
    };

    const handlePrev = () => {
        if (currentStep === 1) {
            setFormData(prev => ({
                ...prev,
                broadcastType: undefined,
                utilityTemplateId: null,
                utilityTemplateData: null,
                secondaryUtilityTemplateId: null,
                secondaryUtilityTemplateData: null,
                selectedPages: [],
                blocks: [],
                selectedEsteiraId: null,
            }));
            setCurrentStep(0);
            return;
        }
        if (currentStep > 1) setCurrentStep(currentStep - 1);
    };

    const handleSubmit = () => {
        if (isLoading) return;
        onSubmit(formData);
    };

    const handleTogglePage = (page: Page) => {
        setFormData(prev => {
            const exists = prev.selectedPages.some(p => p.page_id === page.page_id);
            const selectedPages = exists
                ? prev.selectedPages.filter(p => p.page_id !== page.page_id)
                : [...prev.selectedPages, page];
            return { ...prev, selectedPages };
        });
    };

    const hasBlockContent = (block: ContentBlock): boolean => {
        if (block.type === 'text') return !!block.message?.trim();
        return !!(block.media_url || block.attachment_id);
    };

    const canProceed = (): boolean => {
        if (currentStep === 0) return !!formData.broadcastType;
        if (isEsteira) {
            switch (currentStep) {
                case 1: return formData.selectedEsteiraId != null && !!selectedEsteira;
                case 2: return formData.isScheduled ? !!formData.scheduledTime : true;
                case 3:
                    if (validationResult && !validationResult.all_resolved) return false;
                    return true;
                default: return false;
            }
        }
        if (isUtility) {
            switch (currentStep) {
                case 1:
                    if (useEsteira) return formData.selectedEsteiraId != null && !!selectedEsteira;
                    return !!formData.utilityTemplateId;
                case 2: return formData.isScheduled ? !!formData.scheduledTime : true;
                case 3:
                    if (validationResult && !validationResult.all_resolved) return false;
                    return true;
                default: return false;
            }
        }
        switch (currentStep) {
            case 1: return formData.selectedPages.length > 0;
            case 2: return formData.blocks.length > 0 && formData.blocks.some(hasBlockContent);
            case 3: return formData.isScheduled ? !!formData.scheduledTime : true;
            case 4:
                if (validationResult && !validationResult.all_resolved) return false;
                return true;
            default: return false;
        }
    };

    const isLastStep = currentStep === totalSteps;

    const renderStep = () => {
        if (currentStep === 0) {
            return (
                <StepSelectType
                    onSelect={(type) => {
                        setFormData(prev => ({
                            ...prev,
                            broadcastType: type,
                            utilityTemplateId: null,
                            utilityTemplateData: null,
                            secondaryUtilityTemplateId: null,
                            secondaryUtilityTemplateData: null,
                            selectedPages: [],
                            blocks: [],
                            selectedEsteiraId: null,
                        }));
                        setCurrentStep(1);
                    }}
                />
            );
        }

        if (isEsteira) {
            switch (currentStep) {
                case 1:
                    return (
                        <StepSelectEsteira
                            selectedEsteiraId={formData.selectedEsteiraId ?? null}
                            onSelectedEsteiraChange={(id) => setFormData(prev => ({ ...prev, selectedEsteiraId: id }))}
                        />
                    );
                case 2:
                    return (
                        <StepSchedule
                            isScheduled={formData.isScheduled}
                            scheduledTime={formData.scheduledTime}
                            broadcastType="utility"
                            messageTag={formData.messageTag}
                            utilityTemplateId={formData.utilityTemplateId}
                            pageId={formData.selectedPages[0]?.page_id}
                            onIsScheduledChange={(value) => setFormData({ ...formData, isScheduled: value })}
                            onScheduledTimeChange={(value) => setFormData({ ...formData, scheduledTime: value })}
                            onBroadcastTypeChange={() => {}}
                            onUtilityTemplateChange={() => {}}
                        />
                    );
                case 3:
                    return (
                        <StepReview
                            formData={formData}
                            selectedEsteira={selectedEsteira ?? null}
                            onChange={(aiTranslate, targetLanguage, aiProvider) =>
                                setFormData(prev => ({ ...prev, aiTranslate, targetLanguage, aiProvider }))
                            }
                        />
                    );
                default:
                    return null;
            }
        }

        if (isUtility) {
            switch (currentStep) {
                case 1:
                    return (
                        <StepSelectPage
                            selectedPages={formData.selectedPages}
                            onTogglePage={handleTogglePage}
                            onSetPages={(pages) => setFormData(prev => ({ ...prev, selectedPages: pages }))}
                            broadcastType="utility"
                            utilityTemplateId={formData.utilityTemplateId}
                            onBroadcastTypeChange={() => {}}
                            onUtilityTemplateChange={(id, data) => setFormData(prev => ({
                                ...prev,
                                utilityTemplateId: id,
                                utilityTemplateData: data ? {
                                    id: data.id,
                                    template_name: data.template_name,
                                    title: data.title,
                                    subtitle: data.subtitle,
                                    image_url: data.image_url ?? null,
                                    thumbnail_url: data.thumbnail_url ?? null,
                                    buttons: data.buttons,
                                    approved_count: data.approved_count,
                                } : null,
                            }))}
                            secondaryUtilityTemplateId={formData.secondaryUtilityTemplateId}
                            onSecondaryUtilityTemplateChange={(id, data) => setFormData(prev => ({
                                ...prev,
                                secondaryUtilityTemplateId: id,
                                secondaryUtilityTemplateData: data ?? null,
                            }))}
                        />
                    );
                case 2:
                    return (
                        <StepSchedule
                            isScheduled={formData.isScheduled}
                            scheduledTime={formData.scheduledTime}
                            broadcastType="utility"
                            messageTag={formData.messageTag}
                            utilityTemplateId={formData.utilityTemplateId}
                            pageId={formData.selectedPages[0]?.page_id}
                            onIsScheduledChange={(value) => setFormData({ ...formData, isScheduled: value })}
                            onScheduledTimeChange={(value) => setFormData({ ...formData, scheduledTime: value })}
                            onBroadcastTypeChange={() => {}}
                            onUtilityTemplateChange={(id) => setFormData(prev => ({ ...prev, utilityTemplateId: id }))}
                        />
                    );
                case 3:
                    return (
                        <StepReview
                            formData={formData}
                            selectedEsteira={selectedEsteira ?? null}
                            onChange={(aiTranslate, targetLanguage, aiProvider) =>
                                setFormData(prev => ({ ...prev, aiTranslate, targetLanguage, aiProvider }))
                            }
                        />
                    );
                default:
                    return null;
            }
        }

        switch (currentStep) {
            case 1:
                return (
                    <StepSelectPage
                        selectedPages={formData.selectedPages}
                        onTogglePage={handleTogglePage}
                        onSetPages={(pages) => setFormData(prev => ({ ...prev, selectedPages: pages }))}
                        broadcastType={formData.broadcastType || 'standard'}
                        utilityTemplateId={formData.utilityTemplateId}
                        onBroadcastTypeChange={() => {}}
                        onUtilityTemplateChange={() => {}}
                    />
                );
            case 2:
                return (
                    <StepConfigureBlocks
                        blocks={formData.blocks}
                        onChange={(blocks) => setFormData(prev => ({ ...prev, blocks }))}
                        pageId={formData.selectedPages[0]?.page_id}
                        pageVariables={pageVariables}
                    />
                );
            case 3:
                return (
                    <StepSchedule
                        isScheduled={formData.isScheduled}
                        scheduledTime={formData.scheduledTime}
                        broadcastType={formData.broadcastType || 'standard'}
                        messageTag={formData.messageTag}
                        utilityTemplateId={formData.utilityTemplateId}
                        pageId={formData.selectedPages[0]?.page_id}
                        onIsScheduledChange={(value) => setFormData({ ...formData, isScheduled: value })}
                        onScheduledTimeChange={(value) => setFormData({ ...formData, scheduledTime: value })}
                        onBroadcastTypeChange={(type, tag) => setFormData({ ...formData, broadcastType: type, messageTag: tag, utilityTemplateId: null })}
                        onUtilityTemplateChange={(id) => setFormData(prev => ({ ...prev, utilityTemplateId: id }))}
                    />
                );
            case 4:
                return (
                    <StepReview
                        formData={formData}
                        onChange={(aiTranslate, targetLanguage, aiProvider) =>
                            setFormData(prev => ({ ...prev, aiTranslate, targetLanguage, aiProvider }))
                        }
                    />
                );
            default:
                return null;
        }
    };

    const previewPageLabel = (() => {
        if (formData.selectedPages.length === 0) return null;
        if (formData.selectedPages.length === 1) return formData.selectedPages[0].page_name;
        return `${formData.selectedPages[0].page_name} e mais ${formData.selectedPages.length - 1}`;
    })();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 overflow-hidden">
                <div className="flex h-[95vh]">
                    {/* Left Panel */}
                    <div className="w-2/3 flex flex-col bg-gray-50 border-r border-gray-200">
                        {/* Header */}
                        <div className="bg-white border-b border-gray-200 p-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-3">
                                {isEditMode ? 'Editar Broadcast' : 'Criar Novo Broadcast'}
                            </h2>
                            <Input
                                placeholder="Nome do broadcast (ex: Campanha Black Friday)"
                                value={formData.title || ''}
                                onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                className="mb-4 text-sm"
                            />

                            {/* Progress Steps — oculto no step 0 */}
                            {currentStep > 0 && (
                                <div className="flex items-center justify-between">
                                    {steps.map((step, index) => {
                                        const isActive = currentStep === step.number;
                                        const isCompleted = currentStep > step.number;
                                        return (
                                            <div key={step.number} className="flex items-center flex-1">
                                                <div className="flex flex-col items-center flex-1">
                                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm ${isCompleted ? 'bg-green-500 text-white' : isActive ? 'bg-[#7dd3fc] text-white' : 'bg-gray-200 text-gray-500'}`}>
                                                        {isCompleted ? <Check className="h-5 w-5" /> : step.number}
                                                    </div>
                                                    <p className={`text-xs mt-2 font-medium ${isActive ? 'text-[#0f172a]' : 'text-gray-500'}`}>
                                                        {step.title}
                                                    </p>
                                                </div>
                                                {index < steps.length - 1 && (
                                                    <div className={`h-0.5 flex-1 ${currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'}`} />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Step Content */}
                        <div className="flex-1 overflow-y-auto p-8">
                            {renderStep()}
                        </div>

                        {/* MVP em teste (ms-meta-orchestrator): só aparece no último step,
                            junto do botão de confirmar. Não deve chegar em build de produção
                            enquanto o orquestrador não for validado — ver README do repo. */}
                        {isLastStep && (
                            <div className="border-t border-amber-200 bg-amber-50 px-6 py-3 flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-semibold text-amber-900">Usar orquestrador (MVP em teste)</p>
                                    <p className="text-xs text-amber-700">Dispara via ms-meta-orchestrator em vez do pipeline atual.</p>
                                </div>
                                <Switch
                                    checked={!!formData.useOrchestrator}
                                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, useOrchestrator: checked }))}
                                />
                            </div>
                        )}

                        {/* Footer */}
                        <div className="bg-white border-t border-gray-200 p-6 flex justify-between">
                            <Button
                                variant="outline"
                                onClick={handlePrev}
                                disabled={currentStep === 0 || isLoading}
                            >
                                <ChevronLeft className="mr-2 h-4 w-4" />
                                Anterior
                            </Button>

                            {currentStep === 0 ? (
                                <div />
                            ) : !isLastStep ? (
                                <Button
                                    onClick={handleNext}
                                    disabled={!canProceed() || isLoading || validating}
                                    className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                                >
                                    {validating ? (
                                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando...</>
                                    ) : (
                                        <>Próximo<ChevronRight className="ml-2 h-4 w-4" /></>
                                    )}
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleSubmit}
                                    disabled={!canProceed() || isLoading}
                                    className="bg-green-600 hover:bg-green-700"
                                >
                                    {isLoading
                                        ? (isEditMode ? 'Salvando...' : 'Enviando...')
                                        : (isEditMode ? 'Salvar Alterações' : 'Confirmar e Enviar')
                                    }
                                    <Check className="ml-2 h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Right Panel - Preview */}
                    <div className="w-1/3 bg-white overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
                            <h3 className="font-semibold text-gray-900 text-center">Preview em Tempo Real</h3>
                            {previewPageLabel && (
                                <div className="mt-3 flex items-center gap-2 justify-center text-sm text-gray-600">
                                    <div className="w-6 h-6 bg-[#0f172a] rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                        {formData.selectedPages[0].page_name.substring(0, 2).toUpperCase()}
                                    </div>
                                    <span>{previewPageLabel}</span>
                                </div>
                            )}
                        </div>

                        {validationResult && !validationResult.all_resolved && currentStep >= (isUtility ? 2 : 3) && (
                            <div className="mx-4 mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                                    <p className="text-sm font-semibold text-amber-800">Variáveis faltando</p>
                                </div>
                                <div className="space-y-1 mb-3">
                                    {validationResult.pages.filter(p => !p.can_resolve).map(p => (
                                        <div key={p.id} className="text-xs text-amber-700">
                                            <span className="font-medium">{p.page_name}:</span>{' '}
                                            {p.variables_missing.join(', ')}
                                        </div>
                                    ))}
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="w-full border-amber-400 text-amber-800 hover:bg-amber-100"
                                    onClick={() => setShowCopyDialog(true)}
                                >
                                    Corrigir variáveis
                                </Button>
                            </div>
                        )}

                        {validationResult?.all_resolved && currentStep >= (isUtility ? 2 : 3) && (
                            <div className="mx-4 mt-4 rounded-lg border border-green-200 bg-green-50 p-3 flex items-center gap-2">
                                <Check className="h-4 w-4 text-green-600 shrink-0" />
                                <p className="text-xs text-green-700 font-medium">Todas as variáveis resolvidas</p>
                            </div>
                        )}

                        <div className="p-6">
                            <MessengerPreview
                                blocks={formData.blocks}
                                pageName={formData.selectedPages[0]?.page_name}
                                utilityTemplate={formData.utilityTemplateData ?? null}
                                secondaryUtilityTemplate={formData.secondaryUtilityTemplateData ?? null}
                            />
                        </div>
                    </div>
                </div>
            </DialogContent>

            {showCopyDialog && validationResult && (
                <VariablesCopyDialog
                    open={showCopyDialog}
                    onOpenChange={setShowCopyDialog}
                    pages={validationResult.pages}
                    selectedPages={formData.selectedPages}
                    onFixed={async () => {
                        try {
                            const result = await validateBroadcastVariables({
                                page_ids: formData.selectedPages.map(p => p.id).filter((id): id is number => typeof id === 'number'),
                                blocks: formData.blocks,
                            });
                            setValidationResult(result);
                        } catch {
                            // ignore
                        }
                    }}
                />
            )}
        </Dialog>
    );
}
